import { useEffect, useMemo, useState } from 'react'
import { customerProductsApi, sellerProductsApi } from '../../../services/api'
import type { Customer } from './types'

interface SellerProduct {
  id: number
  product_id: number
  name: string
  presentation: string
  recommended_price: number
  real_price: number
}

interface CustomerProduct {
  id: number
  product_id: number
  name: string
  presentation: string
  recommended_price: number
  real_price: number
}

interface PricingModalProps {
  customer: Customer
  onClose: () => void
}

/** Per-account negotiated pricing for a wholesale customer. Lets the seller
 * pick a product from their OWN catalog (seller_products -- the same list
 * they sell street customers from) and pacta a price for this specific
 * bodega, stored in customer_products. Street pricing is untouched; this is
 * purely the account-level override that create_sale/create_sales_bulk
 * require before a wholesale sale is allowed to go through. */
export default function PricingModal({ customer, onClose }: PricingModalProps) {
  const [catalog, setCatalog] = useState<SellerProduct[]>([])
  const [pactado, setPactado] = useState<CustomerProduct[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [selectedProductId, setSelectedProductId] = useState('')
  const [newPrice, setNewPrice] = useState('')
  const [saving, setSaving] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editingPrice, setEditingPrice] = useState('')

  const fetchAll = async () => {
    setLoading(true)
    try {
      const [catalogRes, pactadoRes] = await Promise.all([
        sellerProductsApi.list(),
        customerProductsApi.list(customer.id),
      ])
      setCatalog(catalogRes.data.seller_products || [])
      setPactado(pactadoRes.data.customer_products || [])
    } catch (err) {
      console.error('Error fetching pricing data:', err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchAll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer.id])

  const pactadoProductIds = useMemo(() => new Set(pactado.map((p) => p.product_id)), [pactado])
  const availableProducts = useMemo(
    () => catalog.filter((p) => !pactadoProductIds.has(p.product_id)),
    [catalog, pactadoProductIds]
  )

  const handleAdd = async () => {
    if (!selectedProductId) {
      setError('Selecciona un producto')
      return
    }
    const priceValue = parseFloat(newPrice)
    if (isNaN(priceValue) || priceValue < 0) {
      setError('Ingresa un precio válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      await customerProductsApi.add(customer.id, { product_id: Number(selectedProductId), real_price: priceValue })
      setSelectedProductId('')
      setNewPrice('')
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al pactar el precio')
    } finally {
      setSaving(false)
    }
  }

  const startEdit = (cp: CustomerProduct) => {
    setEditingId(cp.id)
    setEditingPrice(String(cp.real_price))
  }

  const handleSaveEdit = async (cpId: number) => {
    const priceValue = parseFloat(editingPrice)
    if (isNaN(priceValue) || priceValue < 0) {
      setError('Ingresa un precio válido')
      return
    }
    setSaving(true)
    setError('')
    try {
      await customerProductsApi.update(cpId, { real_price: priceValue })
      setEditingId(null)
      fetchAll()
    } catch (err: any) {
      setError(err.response?.data?.error || 'Error al actualizar el precio')
    } finally {
      setSaving(false)
    }
  }

  const handleRemove = async (cpId: number) => {
    if (!confirm('¿Quitar este precio pactado?')) return
    try {
      await customerProductsApi.remove(cpId)
      fetchAll()
    } catch (err) {
      console.error('Error removing negotiated price:', err)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-cream-50 rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-espresso-200 flex justify-between items-center">
          <h2 className="text-lg font-semibold text-espresso-800">Precios pactados — {customer.name}</h2>
          <button onClick={onClose} className="text-espresso-500 hover:text-espresso-800 text-sm">Cerrar</button>
        </div>
        <div className="px-6 py-4 space-y-4">
          {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

          {loading ? (
            <div className="text-center py-4 text-espresso-500">Cargando...</div>
          ) : (
            <>
              <div className="bg-cream-100 rounded-lg p-3 flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[180px]">
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Producto</label>
                  <select
                    value={selectedProductId}
                    onChange={(e) => setSelectedProductId(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  >
                    <option value="">Selecciona un producto</option>
                    {availableProducts.map((p) => (
                      <option key={p.product_id} value={p.product_id}>
                        {p.name} ({p.presentation}) — precio calle: S/ {p.real_price.toFixed(2)}
                      </option>
                    ))}
                  </select>
                  {availableProducts.length === 0 && catalog.length > 0 && (
                    <p className="mt-1 text-xs text-espresso-500">Ya hay precio pactado para todo tu catálogo con esta cuenta.</p>
                  )}
                  {catalog.length === 0 && (
                    <p className="mt-1 text-xs text-espresso-500">No tienes productos en tu catálogo todavía.</p>
                  )}
                </div>
                <div className="w-32">
                  <label className="block text-xs font-medium text-espresso-500 mb-1">Precio pactado</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={newPrice}
                    onChange={(e) => setNewPrice(e.target.value)}
                    className="w-full border border-espresso-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                  />
                </div>
                <button
                  type="button"
                  onClick={handleAdd}
                  disabled={saving}
                  className="px-4 py-2 bg-terracotta-600 text-white rounded-md text-sm font-medium hover:bg-terracotta-700 disabled:opacity-50"
                >
                  Pactar precio
                </button>
              </div>

              <div className="bg-cream-50 shadow rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-espresso-100">
                  <thead className="bg-cream-100">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Producto</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Precio sugerido</th>
                      <th className="px-4 py-2 text-left text-xs font-medium text-espresso-500 uppercase">Precio pactado</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-espresso-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-espresso-100">
                    {pactado.map((cp) => (
                      <tr key={cp.id}>
                        <td className="px-4 py-3 text-sm text-espresso-800">{cp.name} ({cp.presentation})</td>
                        <td className="px-4 py-3 text-sm text-espresso-500">S/ {cp.recommended_price.toFixed(2)}</td>
                        <td className="px-4 py-3 text-sm text-espresso-800">
                          {editingId === cp.id ? (
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              value={editingPrice}
                              onChange={(e) => setEditingPrice(e.target.value)}
                              className="w-24 border border-espresso-300 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-terracotta-500"
                            />
                          ) : (
                            `S/ ${cp.real_price.toFixed(2)}`
                          )}
                        </td>
                        <td className="px-4 py-3 text-right text-sm space-x-2">
                          {editingId === cp.id ? (
                            <>
                              <button onClick={() => handleSaveEdit(cp.id)} disabled={saving} className="text-terracotta-700 hover:text-terracotta-800 font-medium">Guardar</button>
                              <button onClick={() => setEditingId(null)} className="text-espresso-500 hover:text-espresso-800">Cancelar</button>
                            </>
                          ) : (
                            <>
                              <button onClick={() => startEdit(cp)} className="text-terracotta-700 hover:text-terracotta-800 font-medium">Editar</button>
                              <button onClick={() => handleRemove(cp.id)} className="text-red-600 hover:text-red-700 font-medium">Quitar</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {pactado.length === 0 && (
                  <div className="text-center py-6 text-espresso-500 text-sm">
                    Esta cuenta todavía no tiene precios pactados — no se le puede vender hasta pactar al menos uno.
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
